/* eslint-disable indent */
// SessionToolbar.styled.ts
import {
    AppBar as MuiAppBar,
    Dialog as MuiDialog,
    DialogActions as MuiDialogActions,
    DialogContent as MuiDialogContent,
    DialogContentText as MuiDialogContentText,
    DialogTitle as MuiDialogTitle,
    IconButton as MuiIconButton,
    Menu as MuiMenu,
    MenuItem as MuiMenuItem,
    TextField as MuiTextField,
    Toolbar as MuiToolbar,
} from "@mui/material";
import styled from "styled-components";

import { Button as StyledButton } from "components/atoms/Button";
import { device } from "utils/sizes"; // Your media query sizes
import { colourScheme } from "utils/styling/colours";
import { Text as StyledText } from "utils/styling/typography.styled";

export const AppBar = styled(MuiAppBar)`
    background-color: ${colourScheme.primaryDark};
    border: 1px solid ${colourScheme.primaryLight};
    color: ${colourScheme.white};
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    width: 100%;
    max-width: 90vw;

    ${device.sm} {
        padding: 0.75rem 1.5rem;
    }

    ${device.md} {
        padding: 1rem 2rem;
    }
`;

export const Toolbar = styled(MuiToolbar)`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    max-width: 90vw;

    ${device.md} {
        flex-wrap: nowrap;
        gap: 1rem;
    }
`;

export const FirstRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1.5rem;
    width: 100%;
    flex-wrap: wrap; // Add wrap to allow the button to drop below on smaller screens

    ${device.md} {
        flex-wrap: nowrap; // On larger screens, keep everything in one row
        flex: 1;
        width: auto;
    }
`;

export const SessionColumn = styled.div`
    display: flex;
    flex-direction: column; /* Stack elements in a column */
    gap: 0.5rem; /* Add space between dropdown and button */
    align-items: flex-start; /* Align items to the start */
`;

export const SmallButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    border-radius: 0.25rem;
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    border: 1px solid ${colourScheme.primaryLight};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${colourScheme.primaryLight};
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px ${colourScheme.secondary};
    }

    & svg {
        font-size: 1rem; /* Smaller icon size */
    }
`;

export const SecondRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    width: 100%;

    ${device.md} {
        flex: 1;
        justify-content: flex-end;
        width: auto;
    }
`;

export const CustomIconButton = styled(MuiIconButton)<{
    $isRecording: boolean;
}>`
    font-size: 2rem;
    color: white;
    background-color: ${(props) =>
        props.$isRecording ? colourScheme.error : colourScheme.successDark};
    border-radius: 50%;
    padding: 0.75rem;

    &:hover {
        background-color: ${(props) =>
            props.$isRecording ? colourScheme.error : colourScheme.success};
    }

    ${device.md} {
        font-size: 3rem;
        padding: 1rem;
    }
`;

export const SessionButton = styled(MuiIconButton)`
    font-weight: bold;
    font-family: inherit;
    color: ${colourScheme.white};
    font-size: 1rem;
    border-radius: 0.5rem;

    ${device.md} {
        font-size: 1.2rem;
    }
`;

export const StyledMenu = styled(MuiMenu)`
    & .MuiPaper-root {
        background-color: ${colourScheme.primaryLight};
        color: ${colourScheme.primaryLight};
    }
`;

export const StyledMenuItem = styled(MuiMenuItem)`
    &:hover {
        background-color: ${colourScheme.secondaryDark};
        color: ${colourScheme.white};
    }

    &.Mui-selected {
        background-color: ${colourScheme.secondaryDark};
        color: ${colourScheme.white};
    }

    color: ${colourScheme.white};
    background-color: ${colourScheme.primaryLight};
    font-size: 0.9rem;

    ${device.md} {
        font-size: 1rem;
    }
`;

export const Text = styled(StyledText)`
    color: ${colourScheme.white};
    font-size: 0.9rem;

    ${device.md}) {
        font-size: 1rem;
    }
`;

export const Dialog = styled(MuiDialog)`
    & .MuiPaper-root {
        background-color: ${colourScheme.primaryDark};
        color: ${colourScheme.white};
        border-radius: 0.5rem;
    }
`;

export const DialogTitle = styled(MuiDialogTitle)`
    background-color: ${colourScheme.primaryDark};
    color: ${colourScheme.white};
    border-bottom: 1px solid ${colourScheme.primaryLight};
    padding: 1rem;

    ${device.md} {
        padding: 1.5rem;
    }
`;

export const DialogContent = styled(MuiDialogContent)`
    background-color: ${colourScheme.primaryDark};
    color: ${colourScheme.white};
    padding: 1rem;

    ${device.md} {
        padding: 1.5rem;
    }
`;

export const DialogContentText = styled(MuiDialogContentText)`
    color: ${colourScheme.white};
    margin-bottom: 1rem;
`;

export const TextField = styled(MuiTextField)`
    background-color: ${colourScheme.primaryDark};
    color: ${colourScheme.white};

    & label {
        color: ${colourScheme.white};
    }
    & label.Mui-focused {
        color: ${colourScheme.white};
    }
    & .MuiInputBase-input {
        color: ${colourScheme.white};
    }
    & .MuiOutlinedInput-root {
        background-color: ${colourScheme.primaryDark};
        color: ${colourScheme.white};

        & fieldset {
            border-color: ${colourScheme.primaryLight};
        }

        &:hover fieldset {
            border-color: ${colourScheme.secondary};
        }

        &.Mui-focused fieldset {
            border-color: ${colourScheme.secondary};
        }
    }
`;

export const DialogActions = styled(MuiDialogActions)`
    background-color: ${colourScheme.primaryDark};
    padding: 1rem;
    border-top: 1px solid ${colourScheme.primaryLight};

    ${device.md} {
        padding: 1.5rem;
    }
`;

export const Button = styled(StyledButton)`
    color: ${colourScheme.white};
`;

export const PopoverContentWrapper = styled.div`
    padding: 1rem;
    word-wrap: break-word;
`;

export const PopoverTextBox = styled.div`
    background-color: ${colourScheme.primary};
    padding: 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid ${colourScheme.primaryLight};
    margin: 0.75rem 0;
    font-size: 0.85rem;
    color: ${colourScheme.white};
`;

export const CustomDialog = styled(Dialog)`
    & .MuiPaper-root {
        background-color: ${colourScheme.primaryDark};
        color: ${colourScheme.white};
        border-radius: 0.5rem;
        padding: 2rem;
        width: 100%;
        height: auto;
        max-width: 100vw;
        ${device.md} {
            max-width: 80vw;
            max-height: 80vh;
        }

        ${device.lg} {
            max-width: 60vw;
            max-height: 80vh;
        }

        ${device.xl} {
            max-width: 40vw;
        }
    }
`;

export const CustomDialogTitle = styled(DialogTitle)`
    font-size: 1.5rem;
    border-bottom: 1px solid ${colourScheme.primaryLight};
    padding-bottom: 1rem;
    margin-bottom: 1rem;
`;

export const CustomDialogContent = styled(DialogContent)`
    font-size: 1rem;
    color: ${colourScheme.white};
    padding: 1rem;
    line-height: 1.5;
    overflow-wrap: break-word;
`;

export const CustomDialogActions = styled(DialogActions)`
    padding-top: 1rem;
    border-top: 1px solid ${colourScheme.primaryLight};
`;
